import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Engine, TICK_MS } from '@core/engine';
import { createInitialState } from '@core/state';
import { deserializeBlob } from '@core/save/serialize';
import { WebSaveStorage } from '@platform/webStorage';
import { EngineProvider } from '@ui/store';
import { Site } from '@ui/components/Site';

/**
 * Host layer. Owns the wall clock and the requestAnimationFrame loop, accumulates
 * real elapsed time, and steps the pure core in FIXED increments (TECHNICAL §5).
 * The core never sees rAF, Date.now, or the DOM.
 */
const storage = new WebSaveStorage();
const engine = new Engine(createInitialState({ seed: (Date.now() & 0x7fffffff) >>> 0, now: Date.now() }));

// Debug handle (single-player, client-only): power users + automated QA poke the
// engine. Harmless (no server or authority to subvert), but kept out of the
// public bundle: __BME_DEBUG__ is true only in dev and BME_DEBUG=1 QA builds, so
// the clean production build dead-code-eliminates this whole block.
// __BME_DEBUG__ is declared in src/vite-env.d.ts and injected by vite.config.ts.
if (__BME_DEBUG__) {
  (window as unknown as { __bme?: Engine }).__bme = engine;
}

/** Drive the engine with a fixed-timestep accumulator. Idempotent (StrictMode). */
let tickLoopStarted = false;
function startTickLoop(): void {
  if (tickLoopStarted) return;
  tickLoopStarted = true;
  let last = performance.now();
  let acc = 0;
  const MAX_FRAME_MS = 250; // clamp huge gaps (tab was backgrounded), offline handles the rest
  function frame(now: number): void {
    acc += Math.min(now - last, MAX_FRAME_MS);
    last = now;
    while (acc >= TICK_MS) {
      engine.tick(TICK_MS);
      acc -= TICK_MS;
    }
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

function Root() {
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const blob = await storage.load();
      if (!cancelled && blob) {
        try {
          engine.setState(deserializeBlob(blob));
        } catch (e) {
          console.error('Failed to load save; starting fresh.', e);
        }
      }
      if (!cancelled) {
        setBooted(true);
        startTickLoop();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <EngineProvider engine={engine}>
      <Site storage={storage} booted={booted} />
    </EngineProvider>
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root element');
createRoot(rootEl).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
